import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Sparkles, Heart, Leaf, Star } from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rotating Did You Know Strip Component
const DidYouKnowStrip = () => {
  const tips = [
    "Your vagina is naturally self-cleaning. External care is all you need.",
    "Overwashing can cause more irritation than skipping a wash.",
    "Your pH changes throughout your cycle — that's completely normal.",
    "Gentle care often supports balance better than harsh products."
  ];
  
  const [currentTip, setCurrentTip] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentTip((prev) => (prev + 1) % tips.length);
        setIsVisible(true);
      }, 400);
    }, 5000);

    return () => clearInterval(interval);
  }, [tips.length]);

  return (
    <div className="py-6 bg-bone-white border-y border-peach-light/20">
      <div className="max-w-3xl mx-auto px-4 text-center">
        <p 
          className={`text-sm text-african-earth transition-opacity duration-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <span className="text-perfect-peach font-medium mr-2">Did you know?</span>
          {tips[currentTip]}
        </p>
      </div>
    </div>
  );
};

// Rotating Myth/Truth Card Component
const MythTruthCard = () => {
  const myths = [
    { myth: "Strong soaps mean better hygiene.", truth: "Gentle cleansing protects your natural balance." },
    { myth: "You need daily treatments to stay fresh.", truth: "Intentional care is often better than constant intervention." },
    { myth: "Intimate care should 'fix' your body.", truth: "Your body isn't broken — it just needs support." }
  ];
  
  const [currentMyth, setCurrentMyth] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsVisible(false);
      setTimeout(() => {
        setCurrentMyth((prev) => (prev + 1) % myths.length);
        setIsVisible(true);
      }, 400);
    }, 6000);

    return () => clearInterval(interval);
  }, [myths.length]);

  return (
    <section className="py-12 bg-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className={`bg-bone-white rounded-2xl p-6 flex flex-col sm:flex-row gap-4 sm:gap-8 transition-opacity duration-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <div className="flex-1">
            <p className="text-xs text-african-earth/50 font-medium mb-1 uppercase tracking-wide">Myth</p>
            <p className="text-african-earth/70 line-through decoration-perfect-peach/40 text-sm">{myths[currentMyth].myth}</p>
          </div>
          <div className="flex-1">
            <p className="text-xs text-perfect-peach font-medium mb-1 uppercase tracking-wide">Truth</p>
            <p className="text-[#2D2420] text-sm">{myths[currentMyth].truth}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [peachOfMonth, setPeachOfMonth] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
    fetchPeachOfMonth();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchPeachOfMonth = async () => {
    try {
      const response = await axios.get(`${API}/peach-of-month`);
      if (response.data?.enabled && response.data?.product) {
        setPeachOfMonth(response.data);
      }
    } catch (error) {
      console.error('Error fetching peach of month:', error);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    
    // Get appropriate confirmation message
    let actionText = "added to your routine";
    if (product.is_combo) {
      actionText = "routine started";
    } else if (product.category === "Ritual Reset" || product.category === "Conscious Reset") {
      actionText = "care chosen";
    }
    
    toast.success(`${product.name} ${actionText}!`);
  };

  // Get appropriate CTA text for product card
  const getCtaText = (product) => {
    if (product.is_combo) {
      return "Start My Routine";
    } else if (product.category === "Ritual Reset" || product.category === "Conscious Reset") {
      return "Choose My Care";
    } else {
      return "Add to My Routine";
    }
  };

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Intentional Intimate Care & Wellness Routines | The Perfect Peach Club</title>
        <meta name="description" content="Intentional intimate care products designed to support women with calm, informed wellness routines — from daily care to conscious reset moments. Honouring ancient African traditions." />
        <meta name="keywords" content="intentional intimate care, intimate wellness, intimate care routine, gentle intimate care, conscious self-care for women" />
      </Helmet>
      
      <Navigation />
      <ChatWidget />

      {/* Hero Section - Premium Brand Identity */}
      <section className="relative min-h-screen md:min-h-screen flex items-center overflow-hidden mobile-hero">
        {/* Hero Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_91edb9d9-5d41-4003-a549-830c6def8bd5/artifacts/zjaffalf_athome_product_shoot_1f0fd289-7eae-6c00-8b99-daa3a9016942_0_0.png"
            alt="The Perfect Peach Club products"
            className="w-full h-full object-cover object-center"
          />
          {/* Enhanced gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d]/90 via-[#1a1a1a]/70 to-[#1a1a1a]/30 md:from-[#0d0d0d]/90 md:via-[#1a1a1a]/70 md:to-[#1a1a1a]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d]/70 via-[#0d0d0d]/30 to-[#0d0d0d]/50 md:from-[#0d0d0d]/60 md:via-transparent md:to-[#0d0d0d]/40" />
          <div className="absolute inset-0 bg-[#1a1a1a]/30 md:bg-[#1a1a1a]/20" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full">
          <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 py-8 md:py-40 lg:py-48">
            {/* Mobile: center-aligned, stacked | Desktop: left-aligned */}
            <div className="max-w-2xl mx-auto md:mx-0 flex flex-col items-center md:items-start text-center md:text-left space-y-5 md:space-y-8">
              
              {/* Welcome Label */}
              <p className="text-perfect-peach text-xs md:text-sm font-semibold tracking-[0.25em] md:tracking-[0.35em] uppercase">
                Welcome to
              </p>

              {/* Main Brand Title - Responsive sizing */}
              <h1 className="font-playfair text-[32px] sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-medium text-white leading-[1.1] tracking-tight">
                The Perfect<br />
                <span className="text-perfect-peach">Peach</span> Club
              </h1>

              {/* Supporting Headline - Responsive */}
              <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/95 font-light leading-snug max-w-md md:max-w-none">
                Intentional intimate care for modern women
              </h2>

              {/* Subtext - Two Lines */}
              <div className="space-y-1 pt-1 md:pt-2">
                <p className="text-sm md:text-base lg:text-lg text-white/70 font-light">
                  Calm. Informed. Empowering.
                </p>
                <p className="text-sm md:text-base lg:text-lg text-white/70 font-light">
                  Rooted in African wellness traditions.
                </p>
              </div>

              {/* CTA Buttons - Full width on mobile */}
              <div className="pt-4 md:pt-6 flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto">
                {/* Primary CTA - Full width on mobile */}
                <Link
                  to="/products"
                  className="w-full sm:w-auto inline-flex items-center justify-center bg-perfect-peach text-white px-8 md:px-10 py-4 md:py-5 rounded-full font-semibold text-base md:text-lg hover:bg-white hover:text-perfect-peach transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 min-h-[52px]"
                  data-testid="hero-cta-button"
                >
                  Start My Routine
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                {/* Secondary CTA - Full width on mobile */}
                <Link
                  to="/about"
                  className="w-full sm:w-auto inline-flex items-center justify-center text-white/80 border border-white/30 sm:border-0 px-6 md:px-8 py-4 md:py-5 rounded-full font-medium text-base md:text-lg hover:text-white hover:bg-white/10 transition-all duration-300 min-h-[48px]"
                >
                  Our Story
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="pt-4 md:pt-8 flex flex-col sm:flex-row flex-wrap justify-center md:justify-start gap-3 md:gap-6 text-white/50 text-xs md:text-sm">
                <div className="flex items-center justify-center md:justify-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-perfect-peach/80"></div>
                  <span>Body-respecting formulas</span>
                </div>
                <div className="flex items-center justify-center md:justify-start gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-perfect-peach/80"></div>
                  <span>South African made</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator - Desktop only */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-white/40">
          <span className="text-xs tracking-[0.2em] uppercase font-light">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent animate-pulse"></div>
        </div>
      </section>

      {/* Peach of the Month */}
      {peachOfMonth?.product && (
        <section className="py-16 md:py-20 bg-gradient-to-b from-cream to-bone-white" data-testid="peach-of-month-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8 md:gap-14 items-center">
              {/* Left: product image */}
              <Link
                to={`/product/${peachOfMonth.product.id}`}
                className="relative block group overflow-hidden rounded-3xl shadow-floating"
                data-testid="peach-of-month-image-link"
              >
                <div className="aspect-square bg-white overflow-hidden">
                  <img
                    src={peachOfMonth.product.image_url}
                    alt={peachOfMonth.product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                {/* Corner badge */}
                <div className="absolute top-4 left-4 bg-terracotta text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-soft">
                  <Star className="w-3.5 h-3.5 fill-current" />
                  <span className="text-xs font-semibold uppercase tracking-wider">Peach of the Month</span>
                </div>
              </Link>

              {/* Right: content */}
              <div className="space-y-5 md:pl-4">
                <p className="text-coral text-xs font-semibold uppercase tracking-[0.2em]" data-testid="peach-of-month-tagline">
                  {peachOfMonth.tagline}
                </p>
                <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-medium text-[#2D2420] leading-tight" data-testid="peach-of-month-name">
                  {peachOfMonth.product.name}
                </h2>
                <p className="text-african-earth text-base md:text-lg leading-relaxed max-w-lg">
                  {peachOfMonth.product.description}
                </p>
                <div className="flex items-baseline gap-3 pt-2">
                  <span className="text-3xl font-bold text-perfect-peach" data-testid="peach-of-month-price">
                    R{peachOfMonth.product.price?.toFixed(2)}
                  </span>
                  {peachOfMonth.product.original_price && peachOfMonth.product.original_price > peachOfMonth.product.price && (
                    <>
                      <span className="text-lg text-african-earth/50 line-through">
                        R{peachOfMonth.product.original_price.toFixed(2)}
                      </span>
                      <span className="bg-terracotta/10 text-terracotta text-xs font-semibold px-2.5 py-1 rounded-full">
                        Save R{peachOfMonth.product.savings?.toFixed(2)}
                      </span>
                    </>
                  )}
                  {peachOfMonth.product.size && !peachOfMonth.product.is_combo && (
                    <span className="text-sm text-african-earth/70">· {peachOfMonth.product.size}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 pt-4">
                  <button
                    onClick={() => handleAddToCart(peachOfMonth.product)}
                    className="inline-flex items-center gap-2 bg-terracotta text-white px-7 py-3 rounded-full text-sm font-medium hover:bg-terracotta/90 hover:shadow-floating transition-all"
                    data-testid="peach-of-month-add-to-cart"
                  >
                    {getCtaText(peachOfMonth.product)}
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <Link
                    to={`/product/${peachOfMonth.product.id}`}
                    className="inline-flex items-center gap-2 border border-[#E0D5D0] px-7 py-3 rounded-full text-sm font-medium text-african-earth hover:border-terracotta hover:text-terracotta transition-all"
                    data-testid="peach-of-month-learn-more"
                  >
                    Learn more
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Product Categories - Light, Spacious */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-playfair text-4xl sm:text-5xl font-medium text-[#2D2420]">
              Your Care, Your Way
            </h2>
            <p className="text-lg text-african-earth max-w-2xl mx-auto leading-relaxed">
              From daily support to intentional reset moments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {/* Daily Care Category */}
            <div className="bg-bone-white rounded-3xl p-10 hover:shadow-soft transition-all duration-500">
              <div className="mb-6">
                <div className="w-16 h-16 bg-perfect-peach/10 rounded-full flex items-center justify-center mb-6">
                  <Leaf className="w-8 h-8 text-perfect-peach" />
                </div>
                <h3 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
                  Daily Care
                </h3>
                <p className="text-african-earth leading-relaxed mb-6">
                  External cleansing and hydration designed for gentle, everyday support.
                </p>
              </div>
              <Link
                to="/products"
                className="inline-flex items-center text-perfect-peach font-medium hover:text-[#C57D63] transition-colors duration-300"
              >
                Explore daily care
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>

            {/* Conscious Reset Category */}
            <div className="bg-bone-white rounded-3xl p-10 hover:shadow-soft transition-all duration-500">
              <div className="mb-6">
                <div className="w-16 h-16 bg-royal-gold/10 rounded-full flex items-center justify-center mb-6">
                  <Sparkles className="w-8 h-8 text-royal-gold" />
                </div>
                <h3 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
                  Conscious Reset
                </h3>
                <p className="text-african-earth leading-relaxed mb-6">
                  Occasional and ritual support when your body asks for intentional care.
                </p>
              </div>
              <Link
                to="/products"
                className="inline-flex items-center text-perfect-peach font-medium hover:text-[#C57D63] transition-colors duration-300"
              >
                Choose your care
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products - Minimal Selection */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-playfair text-4xl sm:text-5xl font-medium text-[#2D2420]">
              Our Products
            </h2>
            <p className="text-lg text-african-earth max-w-2xl mx-auto leading-relaxed">
              Designed to support your body with calm, informed wellness
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-3xl overflow-hidden shadow-soft hover:shadow-floating transition-all duration-500 group border border-[#F0EAE6]"
                data-testid={`product-card-${product.id}`}
              >
                <Link to={`/product/${product.id}`}>
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </Link>
                <div className="p-6 space-y-4">
                  <div className="space-y-2">
                    <Link to={`/product/${product.id}`}>
                      <h3 className="font-playfair text-lg font-semibold text-[#2D2420] group-hover:text-perfect-peach transition-colors duration-300">
                        {product.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-african-earth/80">
                      {product.category}
                    </p>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <span className="text-xl font-semibold text-perfect-peach">
                      R{product.price.toFixed(2)}
                    </span>
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="bg-perfect-peach text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#C57D63] transition-all duration-300"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      {getCtaText(product)}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-perfect-peach font-medium text-lg hover:text-[#C57D63] transition-colors duration-300"
              data-testid="view-all-products-link"
            >
              View all products
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section - Simple, Clear */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 space-y-4">
            <h2 className="font-playfair text-4xl sm:text-5xl font-medium text-[#2D2420]">
              Our Approach
            </h2>
            <p className="text-lg text-african-earth leading-relaxed">
              Honouring your body's wisdom with intentional care
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                icon: <Heart className="w-7 h-7" />,
                title: 'Body-respecting',
                description: 'Work with your body, not against it',
              },
              {
                icon: <Leaf className="w-7 h-7" />,
                title: 'Ancient wisdom',
                description: 'Honouring traditional African wellness',
              },
              {
                icon: <Sparkles className="w-7 h-7" />,
                title: 'Informed choice',
                description: 'Calm guidance, never urgency',
              },
            ].map((value, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-perfect-peach/10 rounded-full text-perfect-peach">
                  {value.icon}
                </div>
                <h3 className="font-playfair text-xl font-semibold text-[#2D2420]">
                  {value.title}
                </h3>
                <p className="text-african-earth leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Rotating Did You Know Strip */}
      <DidYouKnowStrip />

      {/* Rotating Myth/Truth Card */}
      <MythTruthCard />

      {/* Final CTA - Soft, Inviting */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8">
          <h2 className="font-playfair text-4xl sm:text-5xl font-medium text-[#2D2420]">
            Your routine begins here
          </h2>
          <p className="text-lg text-african-earth leading-relaxed max-w-2xl mx-auto">
            Join women choosing calm, informed intimate care that honours their body's natural wisdom
          </p>
          <Link
            to="/products"
            className="inline-block bg-perfect-peach text-white px-10 py-5 rounded-full font-medium text-lg hover:bg-[#C57D63] transition-all duration-300 shadow-lg"
            data-testid="cta-shop-button"
          >
            Explore our products
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;